import { Form, Formik } from "formik";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../styles/button";
import { ApiError, FormGroup } from "../../styles/form";
import CustomModal from "../common/modal/CustomModal";
import * as Yup from "yup";
import validations from "../../utils/validations";
import { connect } from "react-redux";
import bucketApi from "../../api/bucket";
import { clientDocumentListLoad } from "../../store/clientDocumentList";
import clientApi from "../../api/client";
import ClientDocumentAddContent, {
  IFormData,
} from "./ClientDocumentAddContent";
import { promiseToastSave } from "../../utils/toasts";
import { errorSet } from "../../utils/error";
import { useParams } from "react-router";
import { keepFileExtensions } from "../../utils/string";

interface IProps {
  file: File | null;
  clientId: number;
  isOpen: boolean;
  close(): void;
  clientDocumentListLoad(reload: boolean): void;
}

const ClientDocumentAddModal: FC<IProps> = ({
  file,
  clientId,
  isOpen,
  close,
  clientDocumentListLoad,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const { folderId } = useParams();

  useEffect(() => {
    setError(null);
  }, [isOpen]);

  const handleSubmit = async (data: IFormData) => {
    setError(null);
    try {
      await promiseToastSave(async () => {
        const fileName = keepFileExtensions(file!.name, data.name);

        const uploadUrl = await clientApi.getClientDocumentUploadUrl(
          clientId,
          folderId,
          fileName,
          null
        );

        await bucketApi.uploadDocument(uploadUrl.data, file!);
        const res = await clientApi.addClientDocument(
          clientId,
          folderId,
          fileName
        );
        if (data.visible) {
          await clientApi.clientDocumentUpdate(res.data.id, {
            name: res.data.name,
            idClient: clientId,
            isClientVisible: true,
          });
        }

        clientDocumentListLoad(true);
        close();
      });
    } catch (err) {
      errorSet(setError, err, t);
    }
  };

  return (
    <Formik
      initialValues={{
        file: "",
        name: "",
        visible: false,
      }}
      validationSchema={Yup.object({
        name: validations.stringRequired(t),
      })}
      validateOnMount={true}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, isValid, submitForm }) => (
        <CustomModal
          title={t("document.addDocument.title")}
          isOpen={isOpen}
          close={close}
          actions={
            <>
              <Button
                ver="secondary"
                disabled={isSubmitting}
                onClick={() => close()}
              >
                {t("common.cancel")}
              </Button>
              <Button
                disabled={!isValid || isSubmitting}
                onClick={() => submitForm()}
              >
                {t("common.save")}
              </Button>
            </>
          }
        >
          <Form>
            <FormGroup>
              <ClientDocumentAddContent file={file} />
              {error && <ApiError>{error}</ApiError>}
            </FormGroup>
          </Form>
        </CustomModal>
      )}
    </Formik>
  );
};

const mapDispachToProps = {
  clientDocumentListLoad,
};

export default connect(null, mapDispachToProps)(ClientDocumentAddModal);
